import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { RootState } from "../../utils/store";
import { uploadFileThunk } from "../file/file.thunk";
import { compressPhoto, formatAdUrl, getFileFromBlob } from "../../utils/utils";
import {
  CreateCampaign,
  CreateCampaignRequest,
  GetAdJoinReport,
  GetAdJoinReportRequest,
  GetAvailableCampaignStartDate,
  GetAvailableCampaignStartDateRequest,
  GetCampaignPaymentToken,
  GetCampaignPaymentTokenRequest,
  GetDefaultCampaignPackages,
  GetDefaultCampaignPackagesRequest,
  GetDefaultCampaignPackagesResponse,
  getPaymentToken,
  GetPaymentTokenRequest,
  GetUserCustomCampaignPackage,
  GetUserCustomCampaignPackageRequest,
  submitAdOrder,
  SubmitBusinessAdOrder,
  SubmitBusinessAdOrderRequest,
} from "./orderAd.api";
import {
  CampaignErrorMessages,
  GetAvailableCampaignStartDateErrorMessages,
  GetCampaignPaymentTokenErrorMessages,
  GetPaymentTokenErrorMessages,
  GetPaymentTokenErrors,
  GetUserCustomCampaignPackageErrorsMessages,
  SubmitAdOrderErrorMessages,
  SubmitBusinessAdOrderErrorMessages,
} from "./orderAd.error";
import { ConnectionErrorToastId } from "../../utils/axios";
import { AppPage, NavigationActions } from "../navigation/navigation.slice";
import { AdType, OrderAdActions, OrderAdInfo } from "./orderAd.slice";
import { strings } from "../../intl/fa";
import { getPaymentBankUrl } from "../../utils/payment";
import { CampaignType } from "./orderAd.entity";
import { sendYandexGoal, YandexGoal } from "../../yandex/yandex";
import { digitsToHindi } from "@bale/utils";

export const submitAdOrderThunk = createAsyncThunk(
  "OrderAd/SubmitAdOrder",
  async (
    request: void | { view_count: number },
    { rejectWithValue, dispatch, getState }
  ) => {
    dispatch(OrderAdActions.setOrderLoading(true));
    const state = getState() as RootState;
    const orderAdInfo: OrderAdInfo = {
      ...state.orderAd.info,
      title: digitsToHindi(state.orderAd.info.title),
      description: digitsToHindi(state.orderAd.info.description),
    };
    const imageBlob = orderAdInfo.imageBlob ?? orderAdInfo.croppedImageURL;

    if (imageBlob) {
      getFileFromBlob(imageBlob, "image.jpeg").then((imageFile) => {
        compressPhoto(imageFile)
          .then((compressedImageFile) => {
            dispatch(
              uploadFileThunk({
                file: compressedImageFile,
                width: orderAdInfo.imageWidth,
                height: orderAdInfo.imageHeight,
              })
            )
              .unwrap()
              .then(async (res) => {
                const body = {
                  ...state.orderAd.info,
                  pic_url: String(res?.file_id),
                  link: state.orderAd.info.link
                    ? formatAdUrl(state.orderAd.info.link)
                    : undefined,
                };

                delete body.imageBlob;
                const { error } = await submitAdOrder(body);
                if (error) {
                  toast.info(SubmitAdOrderErrorMessages[error]);
                  sendYandexGoal(YandexGoal.OrderDialog_error);
                  return rejectWithValue(error);
                }
                toast.info(strings.order_ad.submitted_successfully);
                dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
                dispatch(OrderAdActions.resetState());
                dispatch(OrderAdActions.setOrderLoading(false));
                sendYandexGoal(YandexGoal.OrderDialog_success);
              });
          })
          .catch(() => {
            dispatch(OrderAdActions.setOrderLoading(false));
            toast.info(strings.upload_error, {
              toastId: ConnectionErrorToastId,
            });
            sendYandexGoal(YandexGoal.OrderDialog_error);
          });
      });
    } else {
      // No Photo:
      const body = {
        ...state.orderAd.info,
        link: state.orderAd.info.link
          ? formatAdUrl(state.orderAd.info.link)
          : undefined,
      };
      const { error } = await submitAdOrder(body);
      if (error) {
        toast.info(SubmitAdOrderErrorMessages[error]);
        sendYandexGoal(YandexGoal.OrderDialog_error);
        return rejectWithValue(error);
      }

      toast.info(strings.order_ad.submitted_successfully);
      dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      dispatch(OrderAdActions.resetState());
      dispatch(OrderAdActions.setOrderLoading(false));
      sendYandexGoal(YandexGoal.OrderDialog_success);
    }
  }
);

export const getPaymentTokenThunk = createAsyncThunk(
  "OrderAd/GetPaymentToken",
  async (
    request: GetPaymentTokenRequest & {
      finalPrice: number;
      onSuccess: (url: string) => void;
      onError: () => void;
    },
    { rejectWithValue, dispatch }
  ) => {
    const { ad_id, bonus_code, start_date, start_day, onSuccess, onError } =
      request;
    const { response, error } = await getPaymentToken({
      ad_id,
      bonus_code,
      start_date,
      start_day,
    });
    if (error) {
      if (error === GetPaymentTokenErrors.ErrNeedShahkar) {
        toast.info(GetPaymentTokenErrorMessages[error]);
        dispatch(NavigationActions.setAppPage(AppPage.Invoicing));
        return;
      }
      toast.info(GetPaymentTokenErrorMessages[error]);
      onError();
      sendYandexGoal(YandexGoal.DialogPayment_error);
      return rejectWithValue(error);
    }

    if (response?.token) {
      const link = getPaymentBankUrl(response?.token);
      onSuccess(link);

      dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      sendYandexGoal(YandexGoal.DialogPayment_success, request.finalPrice);
    }
  }
);

export const submitBusinessAdOrderThunk = createAsyncThunk(
  "OrderAd/SubmitBusinessAdOrder",
  async (
    request: SubmitBusinessAdOrderRequest,
    { rejectWithValue, dispatch }
  ) => {
    const { name, phone_no, spot, link } = request;
    const { error } = await SubmitBusinessAdOrder({
      name,
      phone_no,
      spot,
      link,
    });
    if (error) {
      toast.info(SubmitBusinessAdOrderErrorMessages[error]);
      sendYandexGoal(YandexGoal.OrderOnboarding_error);
      return rejectWithValue(error);
    }
    sendYandexGoal(YandexGoal.OrderOnboarding_success);
  }
);

export const createCampaignThunk = createAsyncThunk(
  "OrderAd/CreateCampaign",
  async (request: void, { rejectWithValue, dispatch, getState }) => {
    dispatch(OrderAdActions.setOrderLoading(true));
    const state = getState() as RootState;
    const orderAdInfo = state.orderAd.info;
    const imageBlob = orderAdInfo.imageBlob ?? orderAdInfo.croppedImageURL;
    if (imageBlob) {
      getFileFromBlob(imageBlob, "image.png").then((imageFile) => {
        compressPhoto(imageFile)
          .then((compressedImageFile) => {
            dispatch(uploadFileThunk({ file: compressedImageFile }))
              .unwrap()
              .then(async (res) => {
                const campaignType =
                  state.orderAd.info.adType === AdType.view
                    ? CampaignType.ViewBased
                    : CampaignType.ClickBased;
                const campaignAd = {
                  ...state.orderAd.info,
                  pic_url: String(res?.file_id),
                  type: campaignType,
                  link: state.orderAd.info.link
                    ? formatAdUrl(state.orderAd.info.link)
                    : undefined,
                };
                delete campaignAd.imageBlob;
                delete campaignAd.view_count;
                delete campaignAd.click_count;
                delete campaignAd.desired_start_time;
                const body: CreateCampaignRequest = {
                  ads: [campaignAd],
                  stop_value: orderAdInfo.stop_value,
                };
                const { error } = await CreateCampaign(body);
                if (error) {
                  toast.info(CampaignErrorMessages[error]);
                  dispatch(OrderAdActions.setOrderLoading(false));
                  sendYandexGoal(YandexGoal.OrderCampaign_error);
                  return rejectWithValue(error);
                }
                toast.info(strings.order_ad.submitted_successfully);
                dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
                dispatch(OrderAdActions.resetState());
                dispatch(OrderAdActions.setOrderLoading(false));
                sendYandexGoal(YandexGoal.OrderCampaign_success);
              });
          })
          .catch(() => {
            dispatch(OrderAdActions.setOrderLoading(false));
            toast.info(strings.upload_error, {
              toastId: ConnectionErrorToastId,
            });
            sendYandexGoal(YandexGoal.OrderCampaign_error);
          });
      });
    } else {
      const campaignType =
        state.orderAd.info.adType === AdType.view
          ? CampaignType.ViewBased
          : CampaignType.ClickBased;

      const campaignAd = {
        ...state.orderAd.info,
        type: campaignType,
        link: state.orderAd.info.link
          ? formatAdUrl(state.orderAd.info.link)
          : undefined,
      };
      delete campaignAd.imageBlob;

      delete campaignAd.click_count;
      delete campaignAd.desired_start_time;
      const body: CreateCampaignRequest = {
        ads: [campaignAd],
      };
      const { error } = await CreateCampaign(body);
      if (error) {
        toast.info(CampaignErrorMessages[error]);
        sendYandexGoal(YandexGoal.OrderCampaign_error);
        return rejectWithValue(error);
      }
      toast.info(strings.order_ad.submitted_successfully);
      dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      dispatch(OrderAdActions.resetState());
      dispatch(OrderAdActions.setOrderLoading(false));
      sendYandexGoal(YandexGoal.OrderCampaign_success);
    }
  }
);

export const getAvailableCampaignStartDateThunk = createAsyncThunk(
  "OrderAd/GetAvailableCampaignStartDate",
  async (
    request: GetAvailableCampaignStartDateRequest,
    { rejectWithValue, dispatch }
  ) => {
    const { response, error } = await GetAvailableCampaignStartDate();
    if (error) {
      toast.info(GetAvailableCampaignStartDateErrorMessages[error]);
      return rejectWithValue(error);
    }
    return response;
  }
);

export const GetDefaultCampaignPackagesThunk = createAsyncThunk(
  "OrderAd/GetDefaultCampaignPackages",
  async (
    request: GetDefaultCampaignPackagesRequest,
    { rejectWithValue, dispatch }
  ) => {
    const { response, error } = await GetDefaultCampaignPackages();
    if (error) {
      return rejectWithValue(error);
    }

    dispatch(OrderAdActions.setDefaultCampaignPackages(response));
    return;
  }
);

export const getCampaignPaymentTokenThunk = createAsyncThunk(
  "OrderAd/GetCampaignPaymentToken",
  async (
    request: GetCampaignPaymentTokenRequest & {
      onSuccess: (url: string) => void;
      onError: () => void;
    },
    { rejectWithValue, dispatch }
  ) => {
    const { package_type, onSuccess, onError } = request;
    try {
      const { response, error } = await GetCampaignPaymentToken({
        package_type,
      });

      if (error) {
        toast.info(GetCampaignPaymentTokenErrorMessages[error]);
        onError();
        return rejectWithValue(error);
      }

      if (response?.token) {
        const link = getPaymentBankUrl(response?.token);
        onSuccess(link);
        dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      }
    } catch (error) {
      onError();
    }
  }
);

export const getUserCustomCampaignPackageThunk = createAsyncThunk(
  "OrderAd/GetUserCustomCampaignPackage",
  async (
    request: GetUserCustomCampaignPackageRequest,
    { rejectWithValue, dispatch }
  ) => {
    const { response, error } = await GetUserCustomCampaignPackage();
    if (response?.credit_expire_days) {
      return response;
    }
    if (error) {
      toast.info(GetUserCustomCampaignPackageErrorsMessages[error]);
      return rejectWithValue(error);
    }
  }
);

export const getAdJoinReportThunk = createAsyncThunk(
  "OrderAd/GetJoinReport",
  async (request: GetAdJoinReportRequest, { rejectWithValue, dispatch }) => {
    const { response, error } = await GetAdJoinReport(request);
    if (error) {
      // toast.info(GetUserCustomCampaignPackageErrorsMessages[error]);
      return rejectWithValue(error);
    }
    return response;
  }
);
